import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, throwError, EMPTY } from 'rxjs';
import { LoaderService } from './loader-service';
import { ToastService } from '../toast/toast.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const loaderService = inject(LoaderService);
  const toastService = inject(ToastService);

  // Skip loader only for specific real-time/frequent update endpoints
  const skipLoaderUrls = [
      "/save-multiple-steps",
      "/package-scenarios/",
      "/steps/scenario/",
      "/report/steps/",
      // "/clients/user-clients",
      // "/functional-areas/user-functional-areas",
      "/reference-values/by-code-keys",
      "/report/filtered-summary",
      "/report/stream",
      "/notification-logs"
  ];
  
  const shouldSkipLoader =
    skipLoaderUrls.some(url => req.url.includes(url)) ||
    [
      { path: "/recordings", method: "POST" },
      { path: "/execution-results", method: "POST" },
      { path: "/execution-results/scenario/", method: "GET" },
      { path: "/runner/job/", method: "GET" },
      { path: "/config-data", method: "GET" },
      { path: "/step-generation/generate", method: "POST"},
      { path: "/step-generation/jobs/", method: "GET"},
      { path: "/save-gap-steps", method : "POST"},
      { path : "/gap-results", method : "PATCH"},
      { path : "/gap-results", method : "GET"}
    ].some(rule => req.url.includes(rule.path) && req.method === rule.method);

  if (!shouldSkipLoader) {
    loaderService.start();
  }  // Get token from localStorage or sessionStorage
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  
  // Clone request and add authorization header if token exists
  let authReq = req;
  if (token) {
    authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Skip session-expired handling for login requests — let the login component handle its own errors
      const isLoginRequest = authReq.url.includes('/login') || authReq.url.includes('/auth/saml');

      if (!isLoginRequest && (error.status === 401 || isInvalidToken(error))) {
        handleAuthError(router, toastService);
        return EMPTY;
      }

      return throwError(() => error);
    }),
    finalize(() => {
      // Stop loader when request completes (success or error)
      loaderService.stop();
    })
  );
};

function isInvalidToken(error: HttpErrorResponse): boolean {
  // Check if error message indicates invalid token
  const errorMessage = error.error?.message?.toLowerCase() || '';
  return errorMessage.includes('invalid token') || 
         errorMessage.includes('token expired') ||
         errorMessage.includes('unauthorized');
}

let isRedirecting = false;

function handleAuthError(router: Router, toastService: ToastService): void {
  if (isRedirecting) return;
  isRedirecting = true;

  // Clear stored tokens
  localStorage.removeItem('authToken');
  sessionStorage.removeItem('authToken');

  // Clear all existing toasts and redirect to login first
  toastService.clear();
  router.navigate(['/auth/login']).then(() => {
    toastService.warn('Your session has expired. Please log in again.', 'Session Expired', 'global', 3000);
    isRedirecting = false;
  });
}